import { createApi } from '@reduxjs/toolkit/query/react';
import moment from 'moment';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';
import { ClientListArgument } from 'typings/client';

export const clientApi = createApi({
  tagTypes: [
    'CLIENT',
    'CLIENT_LIST',
    'SHIPPING_ADDRESS',
    'SHIPPING_SPECIAL_RATES',
    'PAYMENT',
    'SPECIAL_PRICING_RULES',
    'SPECIAL_PRICING_RULES_BRAND',
    'SHIPPING_LAST_CHECK',
    'CLIENT_TAGS',
  ],
  reducerPath: 'clientApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    clientCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/client',
        method: 'post',
        body: data,
      }),
      invalidatesTags: () => ['CLIENT_LIST'],
    }),
    clientList: builder.query<any, ClientListArgument>({
      query: ({ page = 1, ...args }) => {
        const number = page - 1;
        return {
          url: `/wrd/api/client/list`,
          params: { ...args, page: number },
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const content = response?.data?.content;
        if (Array.isArray(content)) {
          content[0].clients = (content[0].clients || []).map((item: any) => ({
            ...item,
            formattedLastModifiedDate: {
              full: moment(item.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
              day: moment(item.lastModifiedDate).format('DD.MM.YYYY'),
            },
            formattedCreatedDate: {
              full: moment(item.createdDate).format('DD.MM.YYYY HH:mm:ss'),
              day: moment(item.createdDate).format('DD.MM.YYYY'),
            },
            formattedLastActivityDate: {
              full: item.lastActivityDate ? moment(item.lastActivityDate).format('DD.MM.YYYY HH:mm:ss') : '',
              day: item.lastActivityDate ? moment(item.lastActivityDate).format('DD.MM.YYYY') : '',
            },
            purchaseManager: content[0].managers?.[item.purchaseManagerId] || {},
            salesManager: content[0].managers?.[item.salesManagerId] || {},
            key: item.id,
          }));
        }
        return response;
      },
      providesTags: () => ['CLIENT_LIST'],
    }),
    clientSelectList: builder.query<any, void>({
      query: () => {
        return {
          url: `/wrd/api/client/selector-list`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['CLIENT_LIST'],
    }),
    clientById: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/client/get-by-id?clientId=${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return {
          ...response.data,
          formattedLastModifiedDate: moment(response.data.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
          formattedCreatedDate: moment(response.data.createDate).format('DD.MM.YYYY HH:mm:ss'),
          formattedLastActivityDate: response.data.lastActivityDate
            ? moment(response.data.lastActivityDate).format('DD.MM.YYYY HH:mm:ss')
            : '',
        };
      },
      providesTags: () => ['CLIENT'],
    }),
    clientType: builder.query<any, void>({
      query: () => ({
        url: '/wrd/api/client-type',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data.map((item: any) => ({
          ...item,
          value: item.id,
          label: item.name,
        }));
      },
    }),
    clientDetailsUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/client/update-client-details',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['CLIENT', 'CLIENT_LIST', 'CLIENT_TAGS'],
    }),
    clientContactInfoUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/client/contact-info',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['CLIENT'],
    }),
    clientCorporateAddressUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/client/corporate-address',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['CLIENT'],
    }),
    clientManagement: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/client/client-management',
        method: 'post',
        body: data,
      }),
      invalidatesTags: () => ['CLIENT', 'CLIENT_LIST'],
    }),
    //SHIPPING SPECIAL RATES
    shippingSpecialRates: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/special-shipping-rates?clientId=${id}`,
        };
      },
      transformResponse: (response: any) => {
        return response.data;
      },
      providesTags: () => ['SHIPPING_SPECIAL_RATES'],
    }),
    shippingSpecialRateCreate: builder.mutation<any, any>({
      query: (body) => ({
        url: '/wrd/api/special-shipping-rates',
        method: 'post',
        body,
      }),
      invalidatesTags: () => ['SHIPPING_SPECIAL_RATES', 'SHIPPING_LAST_CHECK', 'CLIENT'],
    }),
    shippingSpecialRateUpdate: builder.mutation<any, any>({
      query: (body) => ({
        url: '/wrd/api/special-shipping-rates',
        method: 'put',
        body,
      }),
      invalidatesTags: () => ['SHIPPING_SPECIAL_RATES', 'SHIPPING_LAST_CHECK', 'CLIENT'],
    }),
    shippingSpecialRateDelete: builder.mutation<any, any>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/special-shipping-rates?id=${id}`,
          method: 'delete',
        };
      },
      invalidatesTags: () => ['SHIPPING_SPECIAL_RATES', 'SHIPPING_LAST_CHECK', 'CLIENT'],
    }),
    specialShippingRatesCountries: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/special-shipping-rates/countries?clientId=${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data.map((item: any) => ({
          ...item,
          cities: item.cities.map((_city: string) => ({
            value: _city,
            label: _city,
          })),
          value: item.id,
          label: item.name,
        }));
      },
      providesTags: () => ['SHIPPING_ADDRESS', 'SHIPPING_SPECIAL_RATES'],
    }),
    shippingAddressCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/shipping-address',
        method: 'post',
        body: data,
      }),
      invalidatesTags: () => ['SHIPPING_ADDRESS', 'SHIPPING_LAST_CHECK', 'CLIENT'],
    }),
    shippingAddress: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/shipping-address?id=${id}`,
        };
      },
      providesTags: () => ['SHIPPING_ADDRESS'],
    }),
    shippingAddressUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/shipping-address',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['SHIPPING_ADDRESS', 'SHIPPING_LAST_CHECK', 'CLIENT'],
    }),
    shippingAddressDelete: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/shipping-address',
        method: 'delete',
        body: data,
      }),
      invalidatesTags: () => ['SHIPPING_ADDRESS', 'SHIPPING_SPECIAL_RATES', 'SHIPPING_LAST_CHECK', 'CLIENT'],
    }),
    shippingAddressLastCheck: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/shipping-address/last-check?id=${id}`,
        };
      },
      providesTags: () => ['SHIPPING_LAST_CHECK'],
    }),
    // PAYMENTS
    paymentGeneralUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/client/update-general-payment',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['CLIENT'],
    }),
    paymentTermsUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/client/update-payment',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['CLIENT'],
    }),
    specialPricingRuleCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/client/create-special-pricing-rules',
        method: 'post',
        body: data,
      }),
      invalidatesTags: () => ['SPECIAL_PRICING_RULES', 'SPECIAL_PRICING_RULES_BRAND', 'CLIENT'],
    }),
    specialPricingRuleUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/client/update-special-pricing-rules',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['SPECIAL_PRICING_RULES', 'SPECIAL_PRICING_RULES_BRAND', 'CLIENT'],
    }),
    specialPricingRule: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/client/get-special-pricing-rules?clientId=${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response?.data?.map((item: any) => ({
          ...item,
          brand: {
            ...item.brand,
            value: item.brand.id,
            label: item.brand.name,
          },
        }));
      },
      providesTags: () => ['SPECIAL_PRICING_RULES'],
    }),
    specialPricingRuleDelete: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/client/delete-special-pricing-rules',
        method: 'delete',
        body: data,
      }),
      invalidatesTags: () => ['SPECIAL_PRICING_RULES', 'SPECIAL_PRICING_RULES_BRAND', 'CLIENT'],
    }),
    specialPricingRuleBrand: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/client/special-pricing-rules/brand?clientId=${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response?.data?.map((item: any) => ({
          ...item,
          value: item.id,
          label: item.name,
        }));
      },
      providesTags: () => ['SPECIAL_PRICING_RULES_BRAND'],
    }),
    clientTags: builder.query<any, void>({
      query: () => ({
        url: '/wrd/api/client/get-tags',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response?.data || [];
      },
      providesTags: () => ['CLIENT_TAGS'],
    }),
    clientPricingRulesUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/client/pricing-rules`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['CLIENT', 'CLIENT_LIST'],
    }),
  }),
});

export const {
  useClientCreateMutation,
  useClientTypeQuery,
  useClientCorporateAddressUpdateMutation,
  useClientListQuery,
  useClientSelectListQuery,
  useClientDetailsUpdateMutation,
  useClientContactInfoUpdateMutation,
  useClientManagementMutation,
  useShippingSpecialRatesQuery,
  useShippingSpecialRateCreateMutation,
  useShippingSpecialRateUpdateMutation,
  useShippingSpecialRateDeleteMutation,
  useShippingAddressCreateMutation,
  useShippingAddressQuery,
  useShippingAddressUpdateMutation,
  useShippingAddressDeleteMutation,
  useShippingAddressLastCheckQuery,
  useClientByIdQuery,
  useSpecialShippingRatesCountriesQuery,
  usePaymentGeneralUpdateMutation,
  usePaymentTermsUpdateMutation,
  useSpecialPricingRuleCreateMutation,
  useSpecialPricingRuleUpdateMutation,
  useSpecialPricingRuleDeleteMutation,
  useSpecialPricingRuleQuery,
  useSpecialPricingRuleBrandQuery,
  useClientPricingRulesUpdateMutation,
  useClientTagsQuery,
} = clientApi;
